import React from 'react';
import { CheckCircle, Circle } from 'lucide-react';

interface EpochSummary {
    epochNumber: number;
    epochName: string;
    epochDate: string;
    status?: string; // e.g. 'completed', 'current', 'planned'
}

interface EpochTimelineProps {
    epochs: EpochSummary[];
    currentEpochId?: number;
    onEpochClick?: (epochNumber: number) => void;
}

const EpochTimeline: React.FC<EpochTimelineProps> = ({ epochs, currentEpochId, onEpochClick }) => {
    return (
        <div className="w-full overflow-x-auto py-6">
            <div className="flex items-center min-w-max px-4">
                {epochs.map((epoch, index) => {
                    const isCurrent = epoch.epochNumber === currentEpochId;
                    const isPast = currentEpochId ? epoch.epochNumber < currentEpochId : false;

                    return (
                        <div key={epoch.epochNumber} className="flex items-center group">
                            {/* Connector Line */}
                            {index > 0 && (
                                <div className={`w-16 h-0.5 mx-2 ${isPast || isCurrent ? 'bg-primary/50' : 'bg-border'}`} />
                            )}

                            {/* Node */}
                            <div
                                className={`
                  relative flex flex-col items-center cursor-pointer transition-all duration-300
                  ${isCurrent ? 'scale-110' : 'hover:scale-105'}
                `}
                                onClick={() => onEpochClick && onEpochClick(epoch.epochNumber)}
                            >
                                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center border-2 
                  ${isCurrent ? 'border-primary bg-primary/20 text-primary shadow-[0_0_15px_rgba(var(--color-primary-rgb),0.5)]' :
                                        isPast ? 'border-primary/50 bg-background text-primary/50' :
                                            'border-border bg-background text-text-secondary'}
                `}>
                                    {isPast ? <CheckCircle size={18} /> :
                                        isCurrent ? <div className="w-3 h-3 bg-primary rounded-full animate-pulse" /> :
                                            <Circle size={18} />}
                                </div>

                                {/* Label */}
                                <div className="absolute top-12 flex flex-col items-center w-32 text-center">
                                    <span className={`text-sm font-semibold ${isCurrent ? 'text-primary' : 'text-text-primary'}`}>
                                        v{epoch.epochNumber}
                                    </span>
                                    <span className="text-xs text-text-secondary truncate w-full">
                                        {epoch.epochName || 'Untitled'}
                                    </span>
                                    <span className="text-[10px] text-text-secondary/60">
                                        {new Date(epoch.epochDate).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Future Node Preview */}
                <div className="flex items-center opacity-50 ml-2">
                    <div className="w-16 h-0.5 mx-2 bg-border border-dashed border-t border-b-0 h-0" />
                    <div className="w-8 h-8 rounded-full border-2 border-border border-dashed flex items-center justify-center text-text-secondary">
                        <span className="text-xs">+</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EpochTimeline;
